import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { User, Banknote, Info, Search, X, ArrowRight, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { GlassCard, Field, GradButton } from "@/components/erp";
import { SelectBox } from "@/components/Inputs";
import { TarihInput } from "@/components/TarihInput";
import { CARI_BOS } from "@/components/CariDialog";
import { AdresListesi, EtiketGirdi } from "@/components/cari/CariAlanlar";
import { DepartmanSecim } from "@/components/cari/DepartmanSecim";
import { CariSekmeler } from "@/pages/Cariler";
import { useAuth } from "@/context/AuthContext";
import { CARI_TIP, PARA, ONAY_KURAL } from "@/lib/perms";
import { PERSONEL_TIP, VERGI_DAIRELERI, secenek } from "@/lib/cari";
import api, { errMsg } from "@/lib/api";

const BOS = { ...CARI_BOS, tip: "", kategori: "genel", sirket_turu: "tuzel", ad: "", soyad: "", kisa_ad: "", mersis_no: "", faks: "", web: "", etiketler: [], adresler: [],
  personel_tipi: "", gorev: "", departman: "", istasyon_id: "", ise_giris: "", isten_cikis: "" };
const ADIMLAR = [["cari", "Cari Bilgileri", User], ["finans", "Finans Bilgileri", Banknote], ["detay", "Cari Detay Bilgileri", Info]];

const Kart = ({ baslik, sag, children, className = "" }) => (
  <GlassCard className={`p-0 ${className}`}><div className="flex items-center gap-3 px-5 py-4 border-b border-white/10"><div className="font-semibold">{baslik}</div><div className="ml-auto">{sag}</div></div><div className="p-5">{children}</div></GlassCard>
);

const Gib = () => {
  const [v, setV] = useState("");
  return (
    <div className="flex" data-testid="gib-sorgu">
      <Input value={v} onChange={(e) => setV(e.target.value)} placeholder="GİB'ten VKN/TCKN Sorgula" className="h-9 w-52 rounded-r-none bg-white/5 text-xs" data-testid="gib-sorgu-input" />
      <button type="button" data-testid="gib-sorgu-btn" onClick={() => toast.info("GİB sorgulama, QNB e-fatura entegrasyonu canlıya alınınca çalışacak.")} className="h-9 px-3 rounded-r-md bg-blue-600 text-white"><Search className="w-4 h-4" /></button>
    </div>
  );
};

const I = ({ k, f, set, type = "text", ph }) => <Input data-testid={`cari-${k}`} type={type} step="any" placeholder={ph} className="bg-white/5" value={f[k] ?? ""} onChange={(e) => set(k, type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} />;

const VergiDairesi = ({ f, set }) => (
  <SelectBox testid="cari-vergi-dairesi" value={f.vergi_dairesi} onChange={(v) => set("vergi_dairesi", v)} placeholder="Vergi dairesi seçin"
    options={secenek(f.vergi_dairesi && !VERGI_DAIRELERI.includes(f.vergi_dairesi) ? [f.vergi_dairesi, ...VERGI_DAIRELERI] : VERGI_DAIRELERI)} />
);

const CariBilgi = ({ f, set }) => {
  const sahis = f.sirket_turu === "sahis";
  return (
    <div className="space-y-4">
      <div><div className="text-xs text-muted-foreground mb-1.5">Şirket Türü *</div>
        <div className="grid grid-cols-2 rounded-lg overflow-hidden border border-white/10" data-testid="cari-sirket-turu">
          {[["sahis", "Şahıs Şirketi"], ["tuzel", "Tüzel Şirket"]].map(([k, l]) => <button key={k} type="button" onClick={() => set("sirket_turu", k)} data-testid={`cari-sirket-${k}`}
            className={`py-2.5 text-sm font-semibold transition-colors ${f.sirket_turu === k ? "bg-blue-600 text-white" : "bg-white/5 text-muted-foreground hover:bg-white/10"}`}>{l}</button>)}
        </div></div>
      <div className="grid sm:grid-cols-3 gap-4">
        {sahis ? <><Field label="Ad *"><I k="ad" f={f} set={set} /></Field><Field label="Soyadı"><I k="soyad" f={f} set={set} /></Field></>
          : <Field label="Ünvan *" className="sm:col-span-2"><I k="unvan" f={f} set={set} /></Field>}
        <Field label="Kısa Ad"><I k="kisa_ad" f={f} set={set} /></Field>
        <Field label={sahis ? "T.C. Kimlik Numarası" : "Vergi Numarası"}><I k="vergi_no" f={f} set={set} /></Field>
        <Field label="Vergi Dairesi"><VergiDairesi f={f} set={set} /></Field>
        <Field label="MERSİS No"><I k="mersis_no" f={f} set={set} /></Field>
      </div>
    </div>
  );
};

const Iletisim = ({ f, set, faks = true }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    <Field label="Telefon"><I k="telefon" f={f} set={set} ph="+90" /></Field>
    {faks && <Field label="Faks"><I k="faks" f={f} set={set} ph="+90" /></Field>}
    <Field label="E-Posta"><I k="email" f={f} set={set} /></Field>
    {faks && <Field label="İnternet Sitesi"><I k="web" f={f} set={set} /></Field>}
  </div>
);

const Finans = ({ f, set, temsilciler }) => {
  const { user } = useAuth();
  const kilit = user.role === "satis";
  const [listeler, setListeler] = useState([]);
  useEffect(() => { api.get("/fiyat-listeleri").then((r) => setListeler(r.data.filter((x) => x.aktif || x.id === f.fiyat_listesi_id))).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="grid sm:grid-cols-3 gap-4">
      {kilit && <p className="sm:col-span-3 text-xs text-amber-400">Risk limiti, bakiye ve kâr oranlarını yalnızca yönetici değiştirebilir.</p>}
      <Field label="Para birimi"><SelectBox testid="cari-para" value={f.para_birimi} onChange={(v) => set("para_birimi", v)} options={PARA} /></Field>
      <fieldset disabled={kilit} className="contents">
        <Field label="Açılış bakiyesi (TL, + alacaklıyız / − borçluyuz)"><I k="acilis_bakiye" f={f} set={set} type="number" /></Field>
        <Field label="Risk limiti (TL, 0 = tanımsız)"><I k="risk_limiti" f={f} set={set} type="number" /></Field>
        <Field label="Satış oranı % (maliyet üzerine)"><I k="kar_yuzde" f={f} set={set} type="number" /></Field>
        <Field label="Yıllık anlaşma iskontosu %"><I k="iskonto_yuzde" f={f} set={set} type="number" ph="0" /></Field>
        <Field label="Fiyat listesi"><SelectBox testid="cari-fiyat-listesi" value={f.fiyat_listesi_id || "yok"} onChange={(v) => set("fiyat_listesi_id", v === "yok" ? "" : v)}
          options={[{ value: "yok", label: "Fiyat listesi yok" }, ...listeler.map((x) => ({ value: x.id, label: `${x.ad} (${x.para_birimi})` }))]} /></Field>
      </fieldset>
      <Field label="Vade (gün)"><I k="vade_gun" f={f} set={set} type="number" /></Field>
      {user.role !== "satis" && <Field label="Satış temsilcisi"><SelectBox testid="cari-temsilci" value={f.temsilci_id || "yok"} onChange={(v) => set("temsilci_id", v === "yok" ? null : v)}
        options={[{ value: "yok", label: "Temsilci yok" }, ...temsilciler.map((t) => ({ value: t.id, label: t.name }))]} /></Field>}
      <Field label="Sipariş onay kuralı"><fieldset disabled={kilit}><SelectBox testid="cari-onay-kurali" value={f.onay_kurali || "varsayilan"} onChange={(v) => set("onay_kurali", v === "varsayilan" ? null : v)}
        options={[{ value: "varsayilan", label: "Firma varsayılanı" }, ...Object.entries(ONAY_KURAL).map(([value, label]) => ({ value, label }))]} /></fieldset></Field>
      <label className="flex items-center gap-2 text-sm"><Switch data-testid="cari-efatura" checked={!!f.efatura_mukellef} onCheckedChange={(v) => set("efatura_mukellef", v)} />e-Fatura mükellefi</label>
      <label className="flex items-center gap-2 text-sm"><Switch data-testid="cari-anlasmali" disabled={kilit} checked={!!f.anlasmali} onCheckedChange={(v) => set("anlasmali", v)} />Anlaşmalı müşteri</label>
      <label className="flex items-center gap-2 text-sm sm:col-span-2"><Switch data-testid="cari-fatura-bakiye" checked={!!f.fatura_bakiye_goster} onCheckedChange={(v) => set("fatura_bakiye_goster", v)} />Fatura açıklamasında cari bakiye göster</label>
      <p className="sm:col-span-3 text-xs text-muted-foreground" data-testid="cari-fiyat-formul">Bu cariye fiyat = maliyet × (1 + %{f.kar_yuzde ?? 20} satış oranı){+f.iskonto_yuzde > 0 ? ` − %${f.iskonto_yuzde} yıllık iskonto` : ""}. Fiyat listesi seçiliyse liste fiyatına iskonto uygulanır.</p>
    </div>
  );
};

const Detay = ({ f, set }) => (
  <div className="grid sm:grid-cols-3 gap-4">
    <Field label="Yetkili kişi"><I k="yetkili" f={f} set={set} /></Field>
    <Field label="GPS enlem"><I k="gps_lat" f={f} set={set} type="number" /></Field>
    <Field label="GPS boylam"><I k="gps_lng" f={f} set={set} type="number" /></Field>
    <Field label="Notlar" className="sm:col-span-3"><textarea data-testid="cari-notlar" rows={3} className="w-full rounded-md border border-input bg-white/5 px-3 py-2 text-sm" value={f.notlar || ""} onChange={(e) => set("notlar", e.target.value)} /></Field>
    <label className="flex items-center gap-2 text-sm"><Switch data-testid="cari-aktif" checked={f.aktif !== false} onCheckedChange={(v) => set("aktif", v)} />Aktif</label>
    <label className="flex items-center gap-2 text-sm sm:col-span-2"><Switch data-testid="cari-teslim-eposta" checked={!!f.teslim_eposta} onCheckedChange={(v) => set("teslim_eposta", v)} />Teslimde imzalı irsaliyeyi e-postayla gönder</label>
  </div>
);

const GenelBilgi = ({ f, set, personel }) => (
  <div className="space-y-4">
    <Field label="Kod"><I k="kod" f={f} set={set} ph="Boş bırakılırsa otomatik" /></Field>
    {personel ? <Field label="Personel Tipi *"><SelectBox testid="cari-personel-tipi" value={f.personel_tipi} onChange={(v) => set("personel_tipi", v)} options={Object.entries(PERSONEL_TIP).map(([value, label]) => ({ value, label }))} /></Field>
      : f.kategori === "potansiyel" ? null : <Field label="Cari Tipi *"><SelectBox testid="cari-tip" value={f.tip} onChange={(v) => set("tip", v)} options={Object.entries(CARI_TIP).filter(([k]) => k !== "personel").map(([value, label]) => ({ value, label }))} /></Field>}
    <Field label="Etiketler"><EtiketGirdi value={f.etiketler} onChange={(v) => set("etiketler", v)} /></Field>
  </div>
);

const PersonelBilgi = ({ f, set }) => (
  <div className="grid sm:grid-cols-3 gap-4">
    <Field label="Ad *"><I k="ad" f={f} set={set} /></Field><Field label="Soyadı"><I k="soyad" f={f} set={set} /></Field><Field label="Kısa Ad"><I k="kisa_ad" f={f} set={set} /></Field>
    <Field label="VKN/TCKN"><I k="vergi_no" f={f} set={set} /></Field>
    <Field label="İşe Giriş Tarihi"><TarihInput data-testid="cari-ise-giris" className="h-10 bg-white/5" value={f.ise_giris} onChange={(e) => set("ise_giris", e.target.value)} /></Field>
    <Field label="İşten Çıkış Tarihi"><TarihInput data-testid="cari-isten-cikis" className="h-10 bg-white/5" value={f.isten_cikis} onChange={(e) => set("isten_cikis", e.target.value)} /></Field>
    <DepartmanSecim f={f} set={set} />
  </div>
);

const Adimlar = ({ adim, setAdim }) => (
  <div className="flex items-center justify-center gap-3 mb-5" data-testid="cari-adimlar">
    {ADIMLAR.map(([k, l, Ic], i) => <div key={k} className="flex items-center gap-3">
      {i > 0 && <div className="hidden sm:block w-24 h-px bg-white/30" />}
      <button type="button" onClick={() => setAdim(i)} data-testid={`cari-adim-${k}`} className={`w-28 sm:w-52 py-3 rounded-lg flex flex-col items-center gap-1 text-xs font-semibold transition-colors ${adim === i ? "bg-blue-600 text-white" : "bg-white/[0.06] hover:bg-white/10"}`}><Ic className="w-5 h-5" />{l}</button>
    </div>)}
  </div>
);

const ayir = (c) => {
  if (c.sirket_turu !== "sahis" || c.ad || c.soyad || !c.unvan) return c;
  const p = c.unvan.trim().split(/\s+/);
  return { ...c, ad: p.slice(0, -1).join(" ") || p[0], soyad: p.length > 1 ? p[p.length - 1] : "" };
};

export default function CariForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState(id ? null : { ...BOS, kategori: params.get("kategori") || "genel", tip: params.get("kategori") === "potansiyel" ? "musteri" : "" });
  const [temsilciler, setTemsilciler] = useState([]);
  const [adim, setAdim] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/temsilciler").then((r) => setTemsilciler(r.data)).catch(() => {});
    if (id) api.get(`/cariler/${id}`).then((r) => setF(ayir({ ...BOS, ...r.data, etiketler: r.data.etiketler?.length ? r.data.etiketler : r.data.grup ? [r.data.grup] : [] }))).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  if (!f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const personel = f.kategori === "personel";
  const sekme = { genel: "genel", potansiyel: "potansiyel", personel: "personel", is_ortagi: "is_ortagi" }[f.kategori];
  const kaydet = async () => {
    if (!personel && f.kategori !== "potansiyel" && !f.tip) { setAdim(0); toast.error("Cari tipini seçin"); return; }
    setBusy(true);
    try {
      const body = { ...f, tip: f.tip || "musteri", risk_limiti: f.risk_limiti || 0, acilis_bakiye: f.acilis_bakiye || 0, kar_yuzde: f.kar_yuzde ?? 20, iskonto_yuzde: f.iskonto_yuzde || 0, vade_gun: f.vade_gun || 0 };
      const { data } = f.id ? await api.put(`/cariler/${f.id}`, body) : await api.post("/cariler", body);
      toast.success(personel ? "Personel kaydedildi" : "Cari kaydedildi"); nav(`/cariler/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const baslik = f.id ? f.unvan : personel ? "Yeni Personel" : f.kategori === "potansiyel" ? "Yeni Potansiyel Müşteri" : f.kategori === "is_ortagi" ? "Yeni İş Ortağı" : "Yeni Genel Cari";
  return (
    <div data-testid="cari-form">
      <CariSekmeler aktif={sekme} />
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h1 className="font-head text-xl font-semibold text-[var(--g1)] uppercase" data-testid="cari-form-baslik">{baslik}</h1>
        <div className="ml-auto flex gap-2">
          <button onClick={() => nav(f.id ? `/cariler/${f.id}` : `/cariler${sekme === "genel" ? "" : `?sekme=${sekme}`}`)} data-testid="cari-iptal" className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg text-sm bg-red-600 text-white hover:bg-red-500"><X className="w-4 h-4" />İptal Et</button>
          <GradButton data-testid="cari-kaydet" disabled={busy} onClick={kaydet}>{busy ? "Kaydediliyor…" : "Kaydet"}</GradButton>
        </div>
      </div>
      {!personel && <Adimlar adim={adim} setAdim={setAdim} />}
      <div className="grid lg:grid-cols-[1fr_380px] gap-4">
        {personel || adim === 0 ? <>
          <Kart baslik="Cari Bilgileri" sag={<Gib />}>{personel ? <PersonelBilgi f={f} set={set} /> : <CariBilgi f={f} set={set} />}</Kart>
          <Kart baslik="Genel Bilgiler"><GenelBilgi f={f} set={set} personel={personel} /></Kart>
          <Kart baslik="Adres Bilgileri"><AdresListesi value={f.adresler} onChange={(v) => set("adresler", v)} /></Kart>
          <Kart baslik="İletişim Bilgileri"><Iletisim f={f} set={set} faks={!personel} /></Kart>
        </> : <Kart className="lg:col-span-2" baslik={ADIMLAR[adim][1]}>{adim === 1 ? <Finans f={f} set={set} temsilciler={temsilciler} /> : <Detay f={f} set={set} />}</Kart>}
      </div>
      {!personel && <div className="flex justify-between mt-4">
        <button disabled={!adim} onClick={() => setAdim(adim - 1)} data-testid="cari-geri" className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg text-sm border border-white/15 disabled:opacity-30"><ArrowLeft className="w-4 h-4" />Geri</button>
        {adim < 2 ? <button onClick={() => setAdim(adim + 1)} data-testid="cari-ileri" className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg text-sm border border-white/15 hover:bg-white/5">İleri<ArrowRight className="w-4 h-4" /></button>
          : <GradButton data-testid="cari-kaydet-alt" disabled={busy} onClick={kaydet}>Kaydet</GradButton>}
      </div>}
    </div>
  );
}
